import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UpcomingOccasions = ({ occasions, onOccasionClick }) => {
  const getUpcomingOccasions = () => {
    const today = new Date();
    const upcoming = occasions
      .filter(occasion => {
        const occasionDate = new Date(occasion.date);
        return occasionDate >= today;
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 5);
    
    return upcoming;
  };

  const getDaysUntil = (dateString) => {
    const today = new Date();
    const occasionDate = new Date(dateString);
    const diffTime = occasionDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    return `${diffDays} days`;
  };

  const getUrgencyLevel = (daysUntil) => {
    if (daysUntil === 'Today') return 'urgent';
    if (daysUntil === 'Tomorrow') return 'high';
    const days = parseInt(daysUntil);
    if (days <= 7) return 'medium';
    return 'low';
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'birthday': return 'Cake';
      case 'anniversary': return 'Heart';
      case 'holiday': return 'Star';
      case 'graduation': return 'GraduationCap';
      case 'wedding': return 'Rings';
      case 'baby': return 'Baby';
      default: return 'Calendar';
    }
  };

  const upcomingOccasions = getUpcomingOccasions();

  if (upcomingOccasions.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-soft border border-accent-100 p-8 text-center">
        <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Calendar" size={32} className="text-accent-500" />
        </div>
        <h3 className="text-lg font-heading font-semibold text-accent-800 mb-2">
          No Upcoming Occasions
        </h3>
        <p className="text-accent-600 mb-4">
          You're all caught up! Add new occasions to stay prepared for future celebrations.
        </p>
        <Button
          variant="primary"
          size="sm"
          iconName="Plus"
          iconPosition="left"
          className="shadow-warm"
        >
          Add Occasion
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-soft border border-accent-100 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-50 to-secondary-50 px-6 py-4 border-b border-accent-100">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-heading font-semibold text-accent-800">
            Upcoming Occasions
          </h2>
          <Button
            variant="ghost"
            size="sm"
            iconName="Calendar"
            className="text-accent-600 hover:text-accent-800"
          >
            View All
          </Button>
        </div>
      </div>

      {/* Occasions List */}
      <div className="divide-y divide-accent-100">
        {upcomingOccasions.map((occasion) => {
          const daysUntil = getDaysUntil(occasion.date);
          const urgencyLevel = getUrgencyLevel(daysUntil);
          
          return (
            <div
              key={occasion.id}
              className="p-6 hover:bg-surface transition-colors duration-200 cursor-pointer"
              onClick={() => onOccasionClick(occasion)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {/* Category Icon */}
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    occasion.category === 'birthday' ? 'bg-secondary-100 text-secondary-600' :
                    occasion.category === 'anniversary' ? 'bg-primary-100 text-primary-600' :
                    occasion.category === 'holiday'? 'bg-warning-100 text-warning-600' : 'bg-accent-100 text-accent-600'
                  }`}>
                    <Icon name={getCategoryIcon(occasion.category)} size={20} />
                  </div>

                  {/* Occasion Details */}
                  <div>
                    <h3 className="font-heading font-semibold text-accent-800 mb-1">
                      {occasion.title}
                    </h3>
                    <div className="flex items-center space-x-3 text-sm text-accent-600">
                      <span className="capitalize">
                        {occasion.category.replace('_', ' ')}
                      </span>
                      {occasion.recipients && occasion.recipients.length > 0 && (
                        <>
                          <span>•</span>
                          <span>{occasion.recipients.join(', ')}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  {/* Gift Status */}
                  <div className="text-right">
                    <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      occasion.giftStatus === 'completed' ? 'bg-success-100 text-success-700' :
                      occasion.giftStatus === 'in_progress'? 'bg-warning-100 text-warning-700' : 'bg-accent-100 text-accent-700'
                    }`}>
                      <Icon 
                        name={
                          occasion.giftStatus === 'completed' ? 'CheckCircle' :
                          occasion.giftStatus === 'in_progress'? 'Clock' : 'AlertCircle'
                        } 
                        size={12} 
                        className="mr-1" 
                      />
                      {occasion.giftStatus === 'completed' ? 'Ready' :
                       occasion.giftStatus === 'in_progress'? 'In Progress' : 'Not Started'}
                    </div>
                  </div>

                  {/* Days Until */}
                  <div className="text-right">
                    <div className={`text-sm font-medium ${
                      urgencyLevel === 'urgent' ? 'text-error-600' :
                      urgencyLevel === 'high' ? 'text-warning-600' :
                      urgencyLevel === 'medium'? 'text-secondary-600' : 'text-accent-600'
                    }`}>
                      {daysUntil}
                    </div>
                    <div className="text-xs text-accent-500">
                      {new Date(occasion.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                      })}
                    </div>
                  </div>

                  {/* Action Button */}
                  <Button
                    variant={urgencyLevel === 'urgent' || urgencyLevel === 'high' ? 'primary' : 'outline'}
                    size="sm"
                    iconName={occasion.giftStatus === 'completed' ? 'Eye' : 'Gift'}
                    className={urgencyLevel === 'urgent' || urgencyLevel === 'high' ? 'shadow-warm' : ''}
                  >
                    {occasion.giftStatus === 'completed' ? 'View' : 'Shop'}
                  </Button>
                </div>
              </div>

              {/* Progress Bar for In Progress */}
              {occasion.giftStatus === 'in_progress' && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-accent-600">Gift Planning Progress</span>
                    <span className="text-xs text-accent-600">67%</span>
                  </div>
                  <div className="w-full bg-accent-100 rounded-full h-2">
                    <div className="bg-warning-400 h-2 rounded-full w-2/3 transition-all duration-300"></div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="bg-surface px-6 py-4 border-t border-accent-100">
        <div className="flex items-center justify-between">
          <p className="text-sm text-accent-600">
            {upcomingOccasions.length} upcoming occasions
          </p>
          <Button
            variant="ghost"
            size="sm"
            iconName="ArrowRight"
            iconPosition="right"
            className="text-accent-600 hover:text-accent-800"
          >
            View Calendar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UpcomingOccasions;